/* eslint-disable max-lines */
import type { Client, Span, SpanAttributes } from '@sentry/core';
import {
  browserPerformanceTimeOrigin,
  debug,
  filterCollectedUrl,
  getActiveSpan,
  getClient,
  getRootSpan,
  hasSpanStreamingEnabled,
  _INTERNAL_performanceTimeToSeconds,
  spanToJSON,
  timestampInSeconds,
  UI_INTERACTION_CLICK_SPAN_NAME_FALLBACK,
  UI_INTERACTION_DRAG_SPAN_NAME_FALLBACK,
  UI_INTERACTION_HOVER_SPAN_NAME_FALLBACK,
  UI_INTERACTION_PRESS_SPAN_NAME_FALLBACK,
  UI_WEBVITAL_CLS_SPAN_NAME_FALLBACK,
  UI_WEBVITAL_LCP_SPAN_NAME_FALLBACK,
} from '@sentry/core';
import { DEBUG_BUILD } from '../debug-build';
import { htmlTreeAsString } from '../htmlTreeAsString';
import { getComponentName } from '../component-name';
import type { InteractionType } from './inp';
import { getCachedInteractionContext, INP_ENTRY_MAP, MAX_PLAUSIBLE_INP_DURATION, UNKNOWN_ELEMENT_NAME } from './inp';
import type { InstrumentationHandlerCallback, MetricNavigationType } from '../instrumentation/performanceObserver';
import {
  addClsInstrumentationHandler,
  addInpInstrumentationHandler,
  addLcpInstrumentationHandler,
} from '../instrumentation/performanceObserver';
import type { LargestContentfulPaint, LayoutShift } from './emitSpan';
import {
  SENTRY_EXCLUSIVE_TIME,
  BROWSER_NAVIGATION_TYPE,
  BROWSER_WEB_VITAL_INP_INTERACTION_TYPE,
  BROWSER_WEB_VITAL_INP_TARGET,
  UI_COMPONENT_NAME,
  SENTRY_OP,
} from '@sentry/conventions/attributes';
import { _emitWebVitalSpan } from './emitSpan';
import { isValidLcpMetric } from './lcp';
import { listenForWebVitalReportEvents } from './reportEvents';
import { getNavigationSpanForMetric } from './softNavs';
import { getBrowserPerformanceAPI, msToSec, supportsWebVital } from '../performance/utils';
import type { PerformanceEventTiming } from '../instrumentation/performanceObserver';
import {
  NAVIGATION,
  UI_INTERACTION_CLICK,
  UI_INTERACTION_DRAG,
  UI_INTERACTION_HOVER,
  UI_INTERACTION_PRESS,
  UI_WEBVITAL_CLS,
  UI_WEBVITAL_LCP,
} from '@sentry/conventions/op';

const INTERACTION_TYPE_TO_SPAN_OP: Record<InteractionType, string> = {
  click: UI_INTERACTION_CLICK,
  hover: UI_INTERACTION_HOVER,
  drag: UI_INTERACTION_DRAG,
  press: UI_INTERACTION_PRESS,
};

const INTERACTION_TYPE_TO_SPAN_NAME_FALLBACK: Record<InteractionType, string> = {
  click: UI_INTERACTION_CLICK_SPAN_NAME_FALLBACK,
  hover: UI_INTERACTION_HOVER_SPAN_NAME_FALLBACK,
  drag: UI_INTERACTION_DRAG_SPAN_NAME_FALLBACK,
  press: UI_INTERACTION_PRESS_SPAN_NAME_FALLBACK,
};

type WebVitalMetric = Parameters<Parameters<typeof addLcpInstrumentationHandler>[0]>[0]['metric'];
type InpMetric = Parameters<InstrumentationHandlerCallback>[0]['metric'];

/**
 * Reports a web vital once per navigation, rather than once per page load.
 *
 * web-vitals restarts the metric on every soft navigation and force-reports the previous one just
 * before it does (and again on pagehide), and re-reports every metric after a bfcache restore.
 * Since `reportAllChanges` is off in this mode, every value we're handed is already the final one
 * for its navigation, so there is nothing to accumulate: each report is a span.
 */
function trackWebVitalPerNavigation<M extends WebVitalMetric>(
  client: Client,
  addInstrumentationHandler: (callback: (data: { metric: M }) => void) => unknown,
  send: (metric: M, parentSpan: Span | undefined, softNavigationId: number | undefined) => void,
): void {
  let pageloadSpan: Span | undefined;
  client.on('afterStartPageLoadSpan', span => {
    pageloadSpan = span;
  });

  // Remembered when the restore happens rather than read back at report time: the restore
  // navigation span is an idle span, and CLS and INP are only finalized on pagehide, by which point
  // it has long ended and is no longer what is active.
  let bfcacheNavigationSpan: Span | undefined;
  client.on('spanStart', span => {
    // The op has to be checked too: the web vital spans emitted for a restore carry the same
    // `back-forward-cache` navigation type, so matching on that alone lets the first of them replace the
    // navigation span, and every later vital then hangs off a sibling vital instead.
    const attributes = spanToJSON(span).attributes;
    if (attributes?.[SENTRY_OP] === NAVIGATION && attributes[BROWSER_NAVIGATION_TYPE] === 'back-forward-cache') {
      bfcacheNavigationSpan = span;
    }
  });

  addInstrumentationHandler(({ metric }) => {
    const navigationSpan = getNavigationSpanForMetric(metric);
    if (metric.navigationType === 'soft-navigation') {
      // Reporting an uncorrelated soft navigation vital would attribute it to the wrong route, so
      // it's dropped instead.
      if (navigationSpan) {
        send(metric, navigationSpan, metric.navigationId);
      } else {
        DEBUG_BUILD &&
          debug.log(`[SoftNav] Dropping ${metric.name} for uncorrelated soft navigation ${metric.navigationId}`);
      }
      return;
    }

    if (metric.navigationType === 'back-forward-cache') {
      // A restore reuses the frozen document, so the pageload span above belongs to the page view
      // from before the freeze. The navigation span started for the restore is the page view these
      // values were actually measured on.
      send(metric, bfcacheNavigationSpan, undefined);
      return;
    }

    send(metric, pageloadSpan, undefined);
  });
}

/**
 * Tracks LCP as a streamed span.
 */
export function trackLcpAsSpan(client: Client, perNavigation = false): void {
  if (!supportsWebVital('largest-contentful-paint')) {
    return;
  }

  if (perNavigation) {
    trackWebVitalPerNavigation(client, addLcpInstrumentationHandler, (metric, parentSpan, softNavigationId) => {
      const entry = metric.entries[metric.entries.length - 1] as LargestContentfulPaint | undefined;
      _sendLcpSpan(
        metric.value,
        entry,
        parentSpan,
        softNavigationId,
        metric.navigationType,
        metric.navigationStartTime,
      );
    });
    return;
  }

  let lcpValue = 0;
  let lcpEntry: LargestContentfulPaint | undefined;
  let lcpNavigationType: MetricNavigationType | undefined;

  const cleanupLcpHandler = addLcpInstrumentationHandler(({ metric }) => {
    // The navigation type describes the page, not the entry, so it is worth keeping even for a
    // report we otherwise discard.
    lcpNavigationType = metric.navigationType;

    const entry = metric.entries[metric.entries.length - 1] as LargestContentfulPaint | undefined;
    if (!entry || !isValidLcpMetric(metric.value)) {
      return;
    }
    lcpValue = metric.value;
    lcpEntry = entry;
  }, true);

  listenForWebVitalReportEvents(client, pageloadSpan => {
    _sendLcpSpan(lcpValue, lcpEntry, pageloadSpan, undefined, lcpNavigationType);
    cleanupLcpHandler();
  });
}

/**
 * Exported only for testing.
 */
export function _sendLcpSpan(
  lcpValue: number,
  entry: LargestContentfulPaint | undefined,
  pageloadSpan?: Span,
  softNavigationId?: number,
  navigationType?: MetricNavigationType,
  navigationStartTime?: number,
): void {
  if (!isValidLcpMetric(lcpValue)) {
    return;
  }

  DEBUG_BUILD && debug.log(`Sending LCP span (${lcpValue})`);

  // A soft navigation's LCP is measured from the triggering interaction, not the document time
  // origin. Starting the span there too keeps it inside the navigation span it is parented to and
  // keeps its duration equal to the reported value. The span's end uses the same origin, even if
  // the time origin was corrected in between.
  const navigationStart = navigationStartTime || 0;
  const startTime = _INTERNAL_performanceTimeToSeconds(navigationStart) ?? msToSec(navigationStart);
  // Without an entry there is no render time to end at, so the span lasts the value it reports,
  // like an entry-less INP does. Ending at the time origin instead would invert the span.
  const endTime = entry
    ? (_INTERNAL_performanceTimeToSeconds(entry.startTime, navigationStart) ?? msToSec(entry.startTime))
    : startTime + msToSec(lcpValue);
  const selector = entry ? htmlTreeAsString(entry.element) : undefined;
  const componentName = entry?.element ? getComponentName(entry.element) : null;
  const client = getClient();
  const hasSpanStreaming = !!client && hasSpanStreamingEnabled(client);
  const name = hasSpanStreaming
    ? componentName || UI_WEBVITAL_LCP_SPAN_NAME_FALLBACK
    : (selector ?? UI_WEBVITAL_LCP_SPAN_NAME_FALLBACK);

  const attributes: SpanAttributes = {};

  if (selector) {
    attributes['browser.web_vital.lcp.element'] = selector;
  }
  if (componentName) {
    attributes[UI_COMPONENT_NAME] = componentName;
  }
  entry?.id && (attributes['browser.web_vital.lcp.id'] = entry.id);
  entry?.url && (attributes['browser.web_vital.lcp.url'] = filterCollectedUrl(entry.url, client));
  entry?.loadTime != null && (attributes['browser.web_vital.lcp.load_time'] = entry.loadTime);
  entry?.renderTime != null && (attributes['browser.web_vital.lcp.render_time'] = entry.renderTime);
  entry?.size != null && (attributes['browser.web_vital.lcp.size'] = entry.size);

  _emitWebVitalSpan({
    name,
    op: UI_WEBVITAL_LCP,
    origin: 'auto.ui.browser.lcp',
    metricName: 'lcp',
    value: lcpValue,
    attributes,
    parentSpan: pageloadSpan,
    startTime,
    endTime,
    softNavigationId,
    navigationType,
  });
}

/**
 * Tracks CLS as a streamed span.
 */
export function trackClsAsSpan(client: Client, perNavigation = false): void {
  if (!supportsWebVital('layout-shift')) {
    return;
  }

  if (perNavigation) {
    trackWebVitalPerNavigation(client, addClsInstrumentationHandler, (metric, parentSpan, softNavigationId) => {
      const entry = metric.entries[metric.entries.length - 1] as LayoutShift | undefined;
      _sendClsSpan(
        metric.value,
        entry,
        parentSpan,
        softNavigationId,
        metric.navigationType,
        metric.navigationStartTime,
      );
    });
    return;
  }

  let clsValue = 0;
  let clsEntry: LayoutShift | undefined;
  let clsNavigationType: MetricNavigationType | undefined;

  const cleanupClsHandler = addClsInstrumentationHandler(({ metric }) => {
    // A CLS of 0 is reported with no entries and still emits a span, so the navigation type has to
    // be captured before the entry check rather than alongside the value.
    clsNavigationType = metric.navigationType;

    const entry = metric.entries[metric.entries.length - 1] as LayoutShift | undefined;
    if (!entry) {
      return;
    }
    clsValue = metric.value;
    clsEntry = entry;
  }, true);

  listenForWebVitalReportEvents(client, pageloadSpan => {
    _sendClsSpan(clsValue, clsEntry, pageloadSpan, undefined, clsNavigationType);
    cleanupClsHandler();
  });
}

/**
 * Exported only for testing.
 */
export function _sendClsSpan(
  clsValue: number,
  entry: LayoutShift | undefined,
  pageloadSpan?: Span,
  softNavigationId?: number,
  navigationType?: MetricNavigationType,
  navigationStartTime?: number,
): void {
  DEBUG_BUILD && debug.log(`Sending CLS span (${clsValue})`);

  // A CLS of 0 has no shift to place the span at. It is reported when the navigation it was
  // measured on is already over - the next soft navigation, or pagehide - so the current time would
  // land it outside that navigation, on the route that follows it.
  const offset = entry?.startTime ?? navigationStartTime ?? 0;
  // CLS is only reported on pagehide, so we use the time origin from when the layout shift happened.
  const startTime = _INTERNAL_performanceTimeToSeconds(offset) ?? timestampInSeconds();
  const firstSourceNode = entry?.sources[0]?.node;
  const selector = entry ? htmlTreeAsString(firstSourceNode) : undefined;
  const componentName = firstSourceNode ? getComponentName(firstSourceNode) : null;
  const client = getClient();
  const hasSpanStreaming = !!client && hasSpanStreamingEnabled(client);
  const name = hasSpanStreaming
    ? componentName || UI_WEBVITAL_CLS_SPAN_NAME_FALLBACK
    : (selector ?? UI_WEBVITAL_CLS_SPAN_NAME_FALLBACK);

  const attributes: SpanAttributes = {};

  if (componentName) {
    attributes[UI_COMPONENT_NAME] = componentName;
  }

  if (entry?.sources) {
    entry.sources.forEach((source, index) => {
      attributes[`browser.web_vital.cls.source.${index + 1}`] = htmlTreeAsString(source.node);
    });
  }

  _emitWebVitalSpan({
    name,
    op: UI_WEBVITAL_CLS,
    origin: 'auto.ui.browser.cls',
    metricName: 'cls',
    value: clsValue,
    attributes,
    parentSpan: pageloadSpan,
    startTime,
    softNavigationId,
    navigationType,
  });
}

/**
 * Tracks INP and emits it as a web vital span, as a child of the interaction's root span.
 * Requires `registerInpInteractionListener()` to be called separately for cached element names and
 * root spans per interaction.
 */
export function trackInpAsSpan(client: Client, perNavigation = false): void {
  const performance = getBrowserPerformanceAPI();
  if (!performance || !browserPerformanceTimeOrigin()) {
    return;
  }

  // INP reports late (on pagehide, after the pageload span has ended). With span streaming enabled
  // it rides the streaming pipeline. With streaming disabled it would be dropped as a late span, so
  // it is emitted as its own standalone v2 span instead (see `_emitWebVitalSpan`), overriding the
  // static trace lifecycle for INP only.
  // TODO(standalone): once the static trace lifecycle is dropped, INP always streams; drop this flag.
  const standalone = !hasSpanStreamingEnabled(client);

  if (perNavigation) {
    // INP restarts per navigation and reports once that navigation is over, by which point the
    // navigation span has ended and the interaction cache no longer knows about it. The metric
    // says which navigation it belongs to, so INP is attributed exactly like LCP and CLS.
    trackWebVitalPerNavigation(client, addInpInstrumentationHandler, (metric, parentSpan, softNavigationId) => {
      if (isPlausibleInp(metric)) {
        _sendInpSpan(metric.value, findInpEntry(metric), standalone, parentSpan, softNavigationId, metric);
      }
    });
    return;
  }

  const onInp: InstrumentationHandlerCallback = ({ metric }) => {
    if (isPlausibleInp(metric)) {
      _sendInpSpan(metric.value, findInpEntry(metric), standalone, undefined, undefined, metric);
    }
  };

  addInpInstrumentationHandler(onInp);
}

function isPlausibleInp(metric: InpMetric): boolean {
  return metric.value != null && msToSec(metric.value) <= MAX_PLAUSIBLE_INP_DURATION;
}

/**
 * The entry an INP span is built from: the one whose duration the reported value came from.
 *
 * There isn't always one. When every interaction of a soft navigation stayed below the Event Timing
 * threshold, web-vitals reports a synthetic value with no entries at all - see
 * `_estimateP98LongestInteraction`. The span still gets reported in that case, just without the
 * element and interaction type an entry would have supplied.
 */
function findInpEntry(metric: InpMetric): PerformanceEventTiming | undefined {
  return metric.entries.find(e => e.duration === metric.value && INP_ENTRY_MAP[e.name]);
}

/**
 * Exported only for testing.
 */
export function _sendInpSpan(
  inpValue: number,
  entry: PerformanceEventTiming | undefined,
  standalone = false,
  attributedSpan?: Span,
  softNavigationId?: number,
  metric?: InpMetric,
): void {
  DEBUG_BUILD && debug.log(`Sending INP span (${inpValue})`);

  // A web vital span carries the metric, not a real interaction timing, so an INP without an entry
  // is still worth reporting. It just has no element or interaction type to describe, and is placed
  // at the start of the navigation it belongs to rather than at the interaction.
  // INP is reported on pagehide, often long after the interaction, so we use the time origin from when the
  // interaction happened.
  const startTime = _INTERNAL_performanceTimeToSeconds(entry?.startTime ?? metric?.navigationStartTime ?? 0);
  if (!startTime) {
    return;
  }
  const duration = msToSec(inpValue);
  // An INP without an entry has no interaction type to report. It still has to land inside the
  // `ui.interaction.*` family, because falling outside it would hide exactly the fast navigations
  // that web-vitals synthesizes these values for (GoogleChrome/web-vitals#724), reintroducing the
  // reporting bias they were added to remove.
  const entryInteractionType = entry && INP_ENTRY_MAP[entry.name];
  const interactionType = entryInteractionType || 'click';

  const cachedContext = entry && getCachedInteractionContext(entry.interactionId);
  const activeSpan = getActiveSpan();
  const rootSpan = activeSpan ? getRootSpan(activeSpan) : undefined;

  // With soft navigations the caller knows exactly which navigation the metric belongs to. Without
  // them we fall back to the span that was active when the interaction was observed.
  const spanToUse = attributedSpan || cachedContext?.span || rootSpan;
  const selector = cachedContext?.elementName || (entry ? htmlTreeAsString(entry.target) : undefined);
  const componentName = entry?.target ? getComponentName(entry.target) : null;
  const client = getClient();
  const hasSpanStreaming = !!client && hasSpanStreamingEnabled(client);
  const fallbackName = INTERACTION_TYPE_TO_SPAN_NAME_FALLBACK[interactionType];
  const name = hasSpanStreaming ? componentName || fallbackName : (selector ?? 'Interaction to next paint');

  const attributes: SpanAttributes = {
    [SENTRY_EXCLUSIVE_TIME]: entry?.duration ?? inpValue,
  };

  // The span's name and op always have a value, even for an INP without an entry, so they can't
  // say whether there was an interaction to describe. These attributes can: they are only set for
  // what was actually observed. The name no longer holds the selector either, now that it is the
  // component name or the op's fallback under span streaming.
  selector && selector !== UNKNOWN_ELEMENT_NAME && (attributes[BROWSER_WEB_VITAL_INP_TARGET] = selector);
  entryInteractionType && (attributes[BROWSER_WEB_VITAL_INP_INTERACTION_TYPE] = entryInteractionType);

  if (componentName) {
    attributes[UI_COMPONENT_NAME] = componentName;
  }

  _emitWebVitalSpan({
    name,
    op: INTERACTION_TYPE_TO_SPAN_OP[interactionType],
    origin: 'auto.ui.browser.inp',
    metricName: 'inp',
    value: inpValue,
    attributes,
    startTime,
    endTime: startTime + duration,
    navigationType: metric?.navigationType,
    parentSpan: spanToUse,
    standalone,
    softNavigationId,
  });
}
